import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Award, Users, TrendingUp } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

const PURE_POUR = '/images/PureWater.png';
const PLANT_IMG = '/images/ROPlant.png';

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav activeItem="About" />

      <section className="py-24 bg-slate-50" data-testid="about-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-accent mb-4">ABOUT US</p>
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-light text-brand-primary font-outfit mb-6">Purity in Every Drop</h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">ZAN Aqua is committed to providing the highest quality RO-filtered water to homes and businesses across the region.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-medium text-brand-primary font-outfit mb-6">Our Story</h2>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                Founded with a mission to make pure, safe drinking water accessible to everyone, ZAN Aqua operates state-of-the-art Reverse Osmosis filtration plants that ensure every drop meets the highest quality standards.
              </p>
              <p className="text-base leading-relaxed text-slate-600">
                Our advanced filtration process removes impurities, harmful chemicals, and contaminants, delivering water that's not just clean, but pure. We believe that access to quality water is a fundamental right, and we're dedicated to making it affordable and convenient.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img src={PURE_POUR} alt="Pure Water" className="w-full" />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 md:order-1"
            >
              <img src={PLANT_IMG} alt="Our Plant" className="w-full" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="order-1 md:order-2"
            >
              <h2 className="text-3xl font-medium text-brand-primary font-outfit mb-6">Our Filtration Process</h2>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                Our Reverse Osmosis (RO) technology uses a semi-permeable membrane to remove ions, unwanted molecules, and larger particles from drinking water. This multi-stage process ensures:
              </p>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start">
                  <Droplets className="h-5 w-5 text-brand-accent mr-3 mt-0.5" />
                  <span>Removal of 99.9% of dissolved solids and impurities</span>
                </li>
                <li className="flex items-start">
                  <Award className="h-5 w-5 text-brand-accent mr-3 mt-0.5" />
                  <span>Certified quality that meets international standards</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp className="h-5 w-5 text-brand-accent mr-3 mt-0.5" />
                  <span>Regular testing and quality control</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 border border-slate-200 text-center"
            >
              <Droplets className="h-12 w-12 text-brand-accent mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brand-primary mb-2">100%</h3>
              <p className="text-slate-600">Pure & Safe Water</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 border border-slate-200 text-center"
            >
              <Users className="h-12 w-12 text-brand-accent mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brand-primary mb-2">10,000+</h3>
              <p className="text-slate-600">Happy Customers</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 border border-slate-200 text-center"
            >
              <TrendingUp className="h-12 w-12 text-brand-accent mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brand-primary mb-2">24/7</h3>
              <p className="text-slate-600">Service Available</p>
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}